import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  meta: string;
  image: string;
}

const slides: HeroSlide[] = [
  {
    id: "m009",
    title: "迷雾之城",
    subtitle: "每一层迷雾之下，都藏着一个不愿被说出的秘密。",
    meta: "悬疑 · 中国 · 2025 · 32集",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20misty%20modern%20metropolis%20at%20night%2C%20warm%20orange%20streetlights%20glowing%20through%20fog%2C%20lone%20figure%20walking%20on%20a%20wet%20street%2C%20moody%20atmospheric%20film%20still%2C%20high%20detail&width=1600&height=900&seq=hero-misty-city-01&orientation=landscape",
  },
  {
    id: "m001",
    title: "星河彼岸",
    subtitle: "在浩瀚星河中，寻找回家的航道，也重新理解了牵挂的含义。",
    meta: "科幻 · 中国 · 2025 · 138分钟",
    image:
      "https://readdy.ai/api/search-image?query=Epic%20cinematic%20space%20scene%2C%20lone%20astronaut%20before%20a%20vast%20glowing%20orange%20and%20teal%20nebula%2C%20distant%20spacecraft%20silhouette%2C%20volumetric%20light%2C%20premium%20film%20still%2C%20high%20detail&width=1600&height=900&seq=hero-starfield-02&orientation=landscape",
  },
  {
    id: "m016",
    title: "笑看风云",
    subtitle: "人生就是一道道选择题，看风云起落，也要笑着前行。",
    meta: "真人秀 · 中国 · 2025 · 12期",
    image:
      "https://readdy.ai/api/search-image?query=Vibrant%20colorful%20TV%20show%20stage%20with%20warm%20orange%20spotlights%2C%20confetti%20in%20the%20air%2C%20energetic%20celebration%20atmosphere%2C%20cinematic%20wide%20film%20still%2C%20high%20detail&width=1600&height=900&seq=hero-variety-stage-03&orientation=landscape",
  },
  {
    id: "m005",
    title: "深海迷航",
    subtitle: "幽暗的深海之下，每一次下潜都可能是有去无回。",
    meta: "冒险 · 美国 · 2025 · 134分钟",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20deep%20ocean%20scene%2C%20sleek%20submersible%20descending%20into%20a%20dark%20trench%20with%20glowing%20blue-green%20creature%20light%2C%20teal%20and%20amber%20contrast%2C%20suspenseful%20film%20still%2C%20high%20detail&width=1600&height=900&seq=hero-deep-sea-04&orientation=landscape",
  },
  {
    id: "m023",
    title: "龙腾九霄",
    subtitle: "剑影江湖，恩怨情仇，真正的九霄不在剑谱，而在心中。",
    meta: "武侠 · 中国 · 2025 · 38集",
    image:
      "https://readdy.ai/api/search-image?query=Epic%20Chinese%20wuxia%20scene%2C%20swordsman%20silhouette%20on%20a%20misty%20mountain%20peak%20at%20golden%20sunrise%2C%20flowing%20robe%2C%20ink-blue%20and%20amber%20tones%2C%20cinematic%20film%20still%2C%20high%20detail&width=1600&height=900&seq=hero-wuxia-peak-05&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(timer);
  }, []);

  const active = slides[index];

  return (
    <section className="relative w-full overflow-hidden bg-secondary-950" aria-label="热门影视焦点图">
      <div className="relative h-[440px] w-full sm:h-[520px] md:h-[600px] lg:h-[640px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== index}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 焦点海报`}
              title={`${slide.title} - 蘑菇视频`}
              className={`h-full w-full object-cover ${i === index ? "animate-ken-burns" : ""}`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/55 to-secondary-950/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/70 via-transparent to-secondary-950/30"></div>

        <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
          <div className="w-full max-w-2xl" key={active.id}>
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                正在热播
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 sm:text-4xl md:text-5xl lg:text-6xl">
                {active.title}
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200 sm:text-base md:text-lg">
                {active.subtitle}
              </p>
              <p className="mt-3 text-xs text-background-300 sm:text-sm">{active.meta}</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                  立即观看
                </Link>
                <Link
                  to={`/detail/${active.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 cursor-pointer"
                >
                  <i className="ri-information-line text-lg" aria-hidden="true"></i>
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到 ${slide.title}`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-primary-500" : "w-3 bg-background-100/50 hover:bg-background-100/80"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 md:left-6 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 sm:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 md:right-6 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 sm:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}